import React, { useState, useEffect, createContext, useContext } from 'react';
import { HashRouter as BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { supabase } from './lib/api';
import { registerServiceWorker } from './lib/push';
import { registerNativePush, unregisterNativePush } from './lib/nativePush';

// Pages
import LoginPage          from './pages/LoginPage';
import SignupPage         from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage  from './pages/ResetPasswordPage';
import DeleteAccountPage  from './pages/DeleteAccountPage';
import DashboardPage  from './pages/DashboardPage';
import WellnessPage   from './pages/WellnessPage';
import WalletPage     from './pages/WalletPage';
import PacksPage      from './pages/PacksPage';
import BookTreatment  from './pages/BookTreatment';
import BiohackPage    from './pages/BiohackPage';
import FitnessPage    from './pages/FitnessPage';
import TrainingPage   from './pages/TrainingPage';
import ProgressPage   from './pages/ProgressPage';
import BookingsPage   from './pages/BookingsPage';
import ProfilePage    from './pages/ProfilePage';
import AdminPage      from './pages/AdminPage';

// ── Auth Context ──────────────────────────────────────────
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    // Register the push notification service worker once on app load
    registerServiceWorker();

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        supabase.rpc('get_my_profile').then(({ data }) => setIsAdmin(data?.is_admin || false));
        // Inside the native app: register this device for push (no-op in a
        // plain browser, where web push handles it instead).
        registerNativePush();
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, s) => {
      setSession(s);
      if (s?.user) {
        supabase.rpc('get_my_profile').then(({ data }) => setIsAdmin(data?.is_admin || false));
        registerNativePush();
      } else {
        setIsAdmin(false);
        // Signed out — drop this device's native token so a logged-out phone
        // stops receiving the previous member's notifications.
        unregisterNativePush();
      }
    });
    return () => subscription.unsubscribe();
  }, []);
  return <AuthContext.Provider value={{ session, loading: session === undefined, isAdmin }}>{children}</AuthContext.Provider>;
}

// ── Protected route ───────────────────────────────────────
function RequireAuth({ children }) {
  const { session, loading } = useAuth();
  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!session) return <Navigate to="/login" replace />;
  return children;
}

// ── SVG Icons ─────────────────────────────────────────────
const ICONS = {
  home: (c) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H5a1 1 0 01-1-1V9.5z"/>
      <path d="M9 21V12h6v9"/>
    </svg>
  ),
  fitness: (c) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 4v16M18 4v16M3 8h3M18 8h3M3 16h3M18 16h3M6 12h12"/>
    </svg>
  ),
  wellness: (c) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s-8-4.5-8-11.8A8 8 0 0112 2a8 8 0 018 8.2C20 17.5 12 22 12 22z"/>
      <path d="M12 8v8M8 12h8"/>
    </svg>
  ),
  training: (c) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="16" height="18" rx="2"/>
      <path d="M9 3V1.5h6V3"/>
      <path d="M8 9h8M8 13h8M8 17h5"/>
    </svg>
  ),
  wallet: (c) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2"/>
      <path d="M16 12a1 1 0 100 2 1 1 0 000-2z" fill={c} stroke="none"/>
      <path d="M2 10h20"/>
    </svg>
  ),
  bookings: (c) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <path d="M16 2v4M8 2v4M3 10h18"/>
      <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/>
    </svg>
  ),
  admin: (c) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  ),
};

// ── Bottom tab bar ────────────────────────────────────────
const MEMBER_TABS = [
  { path: '/',         label: 'Home',    iconKey: 'home'     },
  { path: '/fitness',  label: 'Fitness', iconKey: 'fitness'  },
  { path: '/training', label: 'Training',iconKey: 'training' },
  { path: '/wellness', label: 'Wellness',iconKey: 'wellness' },
  { path: '/wallet',   label: 'Wallet',  iconKey: 'wallet'   },
  { path: '/bookings', label: 'Bookings',iconKey: 'bookings' },
];

function TabBar() {
  const loc = useLocation();
  const { isAdmin } = useAuth();
  // Hide the tab bar on full-screen auth pages (login, signup, password flows).
  const hiddenRoutes = ['/login', '/signup', '/forgot-password', '/reset-password', '/delete-account'];
  if (hiddenRoutes.includes(loc.pathname)) return null;
  const TABS = isAdmin
    ? [...MEMBER_TABS, { path: '/admin', label: 'Admin', iconKey: 'admin' }]
    : MEMBER_TABS;
  return (
    <nav style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, height: 'var(--tab-height)',
      background: '#fff', borderTop: '1px solid #E0E0E0', display: 'flex',
      paddingBottom: 'env(safe-area-inset-bottom)', zIndex: 100,
      boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
    }}>
      {TABS.map(tab => {
        const active = tab.path === '/' ? loc.pathname === '/' : loc.pathname.startsWith(tab.path);
        const colour = active ? 'var(--green)' : '#9E9E9E';
        return (
          <Link key={tab.path} to={tab.path} style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 3, fontSize: '0.6rem', fontWeight: active ? 700 : 500,
            color: colour, textDecoration: 'none', transition: 'color 0.15s',
            letterSpacing: '0.02em', textTransform: 'uppercase',
          }}>
            {ICONS[tab.iconKey](active ? '#FF6200' : '#9E9E9E')}
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}

// ── Top nav bar ───────────────────────────────────────────
function TopNav() {
  const loc = useLocation();
  const { session } = useAuth();
  const hiddenRoutes = ['/login', '/signup', '/forgot-password', '/reset-password'];
  if (hiddenRoutes.includes(loc.pathname)) return null;
  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 99, background: '#1A1A1A',
      minHeight: 'var(--nav-height)',
      paddingTop: 'max(12px, env(safe-area-inset-top))',
      paddingBottom: 12,
      display: 'grid',
      gridTemplateColumns: '48px 1fr 48px',
      alignItems: 'center',
      paddingLeft: 'max(16px, env(safe-area-inset-left))',
      paddingRight: 'max(16px, env(safe-area-inset-right))',
    }}>
      {/* Left spacer */}
      <div />
      {/* Centred logo — matches brand identity */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 3 }}>
        <div style={{ lineHeight: 1 }}>
          <span style={{ color: '#fff', fontWeight: 400, fontSize: '1.05rem', letterSpacing: '0.02em' }}>team </span>
          <span style={{ color: '#fff', fontWeight: 800, fontSize: '1.05rem', letterSpacing: '0.02em' }}>breakthrough</span>
        </div>
        <div style={{ color: '#FF6200', fontWeight: 700, fontSize: '0.55rem', letterSpacing: '0.18em' }}>BOX · BATH · CORSHAM</div>
      </div>
      {/* Profile icon — guests are sent to log in instead of the gated profile page */}
      <Link to={session ? '/profile' : '/login'} style={{ color: '#fff', fontSize: '1.4rem', textAlign: 'right', paddingRight: 4 }}>👤</Link>
    </header>
  );
}

// ── App ───────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <TopNav />
        <Routes>
          <Route path="/login"  element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password"  element={<ResetPasswordPage />} />
          <Route path="/delete-account"  element={<DeleteAccountPage />} />
          {/* Browse routes are public (Apple Guideline 5.1.1(v) — no forced
              registration to view classes, treatments and prices). The
              account-based actions (booking, paying) prompt a login at the
              point of action. */}
          <Route path="/" element={<DashboardPage />} />
          <Route path="/fitness"   element={<FitnessPage />} />
          <Route path="/wellness"  element={<WellnessPage />} />
          <Route path="/wellness/book/:slug" element={<BookTreatment />} />
          <Route path="/wellness/biohack"    element={<BiohackPage />} />
          <Route path="/wallet/packs" element={<PacksPage />} />
          {/* Account-only pages stay behind auth. */}
          <Route path="/wallet"    element={<RequireAuth><WalletPage /></RequireAuth>} />
          <Route path="/training"  element={<RequireAuth><TrainingPage /></RequireAuth>} />
          <Route path="/bookings"  element={<RequireAuth><BookingsPage /></RequireAuth>} />
          <Route path="/profile"   element={<RequireAuth><ProfilePage /></RequireAuth>} />
          <Route path="/progress"  element={<RequireAuth><ProgressPage /></RequireAuth>} />
          <Route path="/admin"     element={<RequireAuth><AdminPage /></RequireAuth>} />
        </Routes>
        {/* Tab bar shows for everyone (including guests) so they can browse;
            tapping a member-only tab prompts login. Hidden on auth screens. */}
        <AuthContext.Consumer>{() => <TabBar />}</AuthContext.Consumer>
      </BrowserRouter>
    </AuthProvider>
  );
}
